<template>
  <div class="company">
    <div v-if="!selectedList" class="no company-card">
      <span>{{ $tc('PleaseSelectList', 1) }}</span>
    </div>
    <div v-if="!!selectedList" class="company-info-container">
      <object-main-info :key="updateObjectMainInfoKey" :fields="fields" :identifier="list ? list.name : ''"
        :identifier-label="$tc('Label', 1)" :editing-data="editingData"
        :show-edit-button="actionToExecute !== 'createCustomList' && (tableActiveTab !== 0 || isSuperAdmin)" :card-label="cardLabel" :height="height"
        :submit-button-label="submitButtonLabel" :required-fields-filled="requiredFieldsAreFilled"
        @toggle-edit="toggleEdit" @update-object="updateList">
      </object-main-info>
      <lists-specific-info v-if="!showPermissions" :editingData="editingData" :list="list ? list : []"
        @cancel-edit="discardChangesDialogIsOpen = true" @save-material="saveChangesDialogIsOpen = true"
        :required-fields-filled="requiredFieldsAreFilled" :entries="entriesData ? entriesData : []"
        :tableActiveTab="tableActiveTab" :workspaces="list ? list.workspaces : []"
        :hasChanges="detectedChanges"
        :can-edit-system-list="isSuperAdmin"
        :deleteActionButtonDisable="tableActiveTab === 1 ? false : true" :actionToExecute="actionToExecute" :mainCardHeight="'60%'" />
    </div>

    <loading-spinner :active.sync="loading" :is-full-page="false" color="#29BDF0" style="position: absolute;" />
    <dialog-choice :isOpen="saveChangesDialogIsOpen" :title="$t('SaveChanges')" :content="$t('SaveChangesContent')"
      :mainActionLabel="$t('Confirm')" @mainAction="saveObject" :secondaryActionLabel="$t('Cancel')"
      @secondaryAction="saveChangesDialogIsOpen = false" icon="mdi-content-save" iconColor="#29BDF0" />
    <dialog-choice :isOpen="discardChangesDialogIsOpen" :title="$t('DiscardChanges')"
      :content="$t('ClusterCreationConfirmation')" :mainActionLabel="$t('Confirm')" @mainAction="cancelEdit"
      :secondaryActionLabel="$t('Cancel')" @secondaryAction="discardChangesDialogIsOpen = false" icon="mdi-delete"
      iconColor="#29BDF0" />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { bus } from "@/main.js";
import _ from 'lodash';
import utils from "@/utils";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import { objectManagementMixin } from "@/mixins/objectManagementMixin";
import LoadingSpinner from "vue-loading-overlay";
import ObjectMainInfo from '@/newComponents/_Organisms/ObjectMainInfo';
import ListsSpecificInfo from '@/newComponents/SystemManagement/Lists/ListsSpecificInfo';
import DialogChoice from '../../_Molecules/DialogChoice.vue';
import CustomButton from '@/newComponents/_Atoms/CustomButton';
import PermissionMatrix from '@/newComponents/_Atoms/PermissionMatrix';
import { rolesListsMixin } from "@/mixins/management/rolesListsMixin";

export default {
  name: "Lists",

  components: {
    LoadingSpinner,
    DialogChoice,
    ObjectMainInfo,
    CustomButton,
    ListsSpecificInfo,
    PermissionMatrix,
  },

  mixins: [checkPermissionsMixin, objectManagementMixin, rolesListsMixin],

  props: {
    selectedList: {
      type: Object,
    },
  },

  data() {
    return {
      loading: false,
      updateObjectMainInfoKey: 1,
      list: null,
      editingData: false,
      entriesHasChanges: false,
      entriesHasDeletions: false,
      entriesHasAdditions: false,
      detectedChanges: false,
      changedEntries: [],
      newEntries: [],
      permissionDisable: false,
      roleLevelOptions: [],
      rolesToCopyFrom: [],
      originalList: {},
      copyFromDisable: false,
      showPermissions: false,
      dataToSend: {},
      dataToSendEntries: {},
      actionToExecute: 'editCustomList',
      submitButtonLabel: 'Save',
      cardLabel: 'EditList',
      saveChangesDialogIsOpen: false,
      discardChangesDialogIsOpen: false,
      tableActiveTab: 0,
      entriesData: [],
      originalEntriesData: [],
    }
  },

  mounted() {
    bus.$on("copyFromInputBus", (event) => {
      this.copyFromFunction(event)
    })
    bus.$on("activeTab-change", (event) => {
      this.onTabChange(event)
    })
  },

  destroyed() {
    bus.$off("copyFromInputBus");
    bus.$off("activeTab-change");
  },

  watch: {
    selectedList(value) {
      if (value !== null && Object.keys(value).length === 0) {
        this.setCreateListVariables();
      } else if (!!value) {
        this.setEditListVariables();
        this.getEntries(this.tableActiveTab)
      } else if (value === null) {
        this.clearVariables();
      }
    },

    entriesData: {
      handler(value) {
        this.entriesHasAdditions = false;
        this.entriesHasChanges = false;
        this.entriesHasDeletions = false;
        this.changedEntries = [];
        this.newEntries = [];
        this.deletedEntries = [];

        if (Array.isArray(value) && Array.isArray(this.originalEntriesData)) {
          if (value.length !== this.originalEntriesData.length) {
            if (value.length > this.originalEntriesData.length) {
              this.entriesHasAdditions = true;
            } else {
              this.entriesHasDeletions = true;
            }
          }

          for (let i = 0; i < value.length; i++) {
            if (i < this.originalEntriesData.length) {
              if (this.isDifferent(value[i], this.originalEntriesData[i])) {
                this.entriesHasChanges = true;
                this.changedEntries.push(value[i]);
              }
            } else {
              this.entriesHasAdditions = true;
              this.newEntries.push(value[i]);
            }
          }

          for (let i = 0; i < this.originalEntriesData.length; i++) {
            if (!value.some(entry => this.isDifferent(entry, this.originalEntriesData[i]) === false)) {
              this.entriesHasDeletions = true;
              this.deletedEntries.push(this.originalEntriesData[i]);
            }
          }
        } else {
          console.error('Data error');
        }

        if (this.entriesHasAdditions) {
          this.detectedChanges = true
        }
        if (this.entriesHasChanges) {
          this.detectedChanges = true
        }
        if (this.entriesHasDeletions) {
          this.detectedChanges = true
        }
        if (!this.entriesHasAdditions && !this.entriesHasChanges && !this.entriesHasDeletions) {
          this.detectedChanges = false
        }
      },
      deep: true
    },

    editingData() {
      bus.$emit("toggle-create-edit-flag")
    },
  },

  computed: {
    requiredFieldsAreFilled() {
      return this.list ? this.actionToExecute === 'createCustomList' ? !!this.list.name && this.list.name.length > 1 : (this.entriesHasAdditions || this.entriesHasChanges || this.entriesHasDeletions) || (this.list.definition !== this.originalList.definition || this.list.hidden !== this.originalList.hidden ) : false
    },

    height() {
      if (this.editingData) {
        return '37%'
      } else {
        return '20%'
      }
    },

    creatingList() {
      return this.actionToExecute === 'createCustomList';
    },

    fields() {
      let fields = []
      const disableSystemListFields = this.tableActiveTab === 0 && !this.isSuperAdmin;
      fields = [
        {
          icon: "block",
          label: this.$i18n.t("Hidden"),
          value: this.list ? (this.list.hidden ? 'True': 'False') : null,
          varName: 'hidden',
          inputType: 'radio',
          disabled: disableSystemListFields,
          required: false,
          radioOptions: [{ value: 'True', label: this.$i18n.t('Yes') }, { value: 'False', label: this.$i18n.t('No') }],
          size: '100%',
        },
        {
          icon: "subject",
          label: this.$i18n.t("Description"),
          value: this.list ? this.list.definition : null,
          varName: 'definition',
          inputType: 'textarea',
          disabled: disableSystemListFields,
          required: false,
          size: '100%',
        },
      ]

      if (this.actionToExecute === 'createCustomList') {
        fields.splice(0, 0,
          {
            icon: "label",
            label: this.$i18n.tc("Label", 1),
            value: this.list.name,
            varName: 'name',
            inputType: 'text',
            disabled: this.tableActiveTab === 0 ? true : false,
            required: true,
            size: '100%',
            validation: {
              validation: 'length',
              min: 2,
              max: 40
            }
            // validation: {

            // }
          },
        )
      }
      if (this.tableActiveTab === 0) {
        fields.splice(0, 0,
          {
            icon: "code",
            label: this.$i18n.tc("Code", 1),
            value: this.list ? this.list.code : null,
            varName: 'code',
            inputType: 'text',
            disabled: disableSystemListFields,
            required: true,
            size: '100%',
          },
        )
      }

      return fields
    },
  },

  methods: {
    ...mapActions([
      "GET_LIST",
      "fetchPermissionsMatrix",
      "fetchAllClientRolesParams",
      "fetchCustomListByUuid",
    ]),

    copyFromFunction(event) {
      const roleToCopy = this.rolesToCopyFrom.find(role => role.id === event);
      this.list.description = roleToCopy.description
      this.list.name = roleToCopy.name
      this.list.role_level = roleToCopy.role_level
      this.list.permissions = roleToCopy.permissions
    },

    onTabChange(event) {
      this.tableActiveTab = event
      // this.getEntries(event)
    },

    async getEntries(tab) {
      this.loading = true;
      try {
        if (tab === 0) {
          let modifiedCode = this.list.code.replace(/^l_/, 'list_');
          const result = await this.GET_LIST({ listName: modifiedCode });
          this.entriesData = result
          this.originalEntriesData = _.cloneDeep(this.entriesData);
        }
        if (tab === 1) {
          const result = await this.fetchCustomListByUuid(this.list.uuid)
          this.entriesData = result.entries
          this.originalEntriesData = _.cloneDeep(this.entriesData);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async getListByID() {
      this.list = this.selectedList
      this.originalList = _.cloneDeep(this.list);
    },

    updateList(updatedValue) {
      this.list[updatedValue.varName] = updatedValue.value;
    },

    cancelEdit() {
      this.discardChangesDialogIsOpen = false;
      this.editingData = false;
      if (this.list.uuid) {
        this.list = _.cloneDeep(this.originalList);
        this.entriesData = _.cloneDeep(this.originalEntriesData);
      } else {
        this.$emit('clear-selected-item');
      }
    },

    toggleEdit() {
      if (this.editingData) {
        this.discardChangesDialogIsOpen = true;
      } else {
        this.editingData = !this.editingData
      }
    },

    async saveObject() {
      this.saveChangesDialogIsOpen = false;
      try {
        this.loading = true;

        this.createDataToSend();

        if (Object.keys(this.dataToSend).length > 0) {
          if (!this.creatingList && this.actionToExecute === 'editCustomList') {
            this.dataToSend.uuid = this.list.uuid;
            await this.$store.dispatch(this.actionToExecute, {newData: this.dataToSend , listUuid: this.dataToSend.uuid});

          } else {
            await this.$store.dispatch(this.actionToExecute, this.dataToSend);
          }
        }

        this.editingData = false;

        if (!this.creatingList) {
          this.getListByID();
          this.$toasted.global.toast_success(this.$i18n.t('ListUpdatedSuccessfully'));
        } else {
          this.$toasted.global.toast_success(this.$i18n.t('ListCreatedSuccessfully'));
        }

        bus.$emit('reload-companies');
      } catch (error) {
        this.handleError(error);
      } finally {
        this.loading = false;
        this.dataToSend = {};
      }
    },

    isDifferent (obj1, obj2) {
      return JSON.stringify(obj1) !== JSON.stringify(obj2);
    },

    createDataToSend() {
      if (this.actionToExecute === 'editCustomList') {
        this.dataToSend = this.list;
        delete this.dataToSend.originalEntriesData;
        this.dataToSend.entries = this.entriesData;
      }
      if (this.actionToExecute === 'createCustomList') {
        this.dataToSend = this.list;
        delete this.dataToSend.originalEntriesData;
        this.dataToSend.entries = this.entriesData;
      }
    },

    handleError(error) {
      let errorHandle = utils.errorHandler(error);
      if (typeof errorHandle === "object") {
        if (errorHandle.hasOwnProperty("email")) {
          this.$toasted.global.toast_error(this.$i18n.t('ValidEmail'));
        }
        if (errorHandle.hasOwnProperty("address")) {
          this.$toasted.global.toast_error(this.$i18n.t('ValidAdress'));
        }
        if (errorHandle.hasOwnProperty("name")) {
          this.$toasted.global.toast_error(this.$i18n.t('ValidName'));
        }
        if (errorHandle.hasOwnProperty("image")) {
          this.$toasted.global.toast_error(this.$i18n.t('ValidImage'));
        }
        if (errorHandle.hasOwnProperty("company_id")) {
          this.$toasted.global.toast_error(this.$i18n.t('ValidParentCompany'));
        }
      } else if (typeof errorHandle === "string") {
        this.$toasted.global.toast_error(errorHandle);
      }
    },

    setCreateListVariables() {
      this.list = {
        hidden: false,
        label: '',
        name: ''
      };
      this.entriesData = [];
      this.originalList = {};
      this.actionToExecute = 'createCustomList'; // action to create
      this.submitButtonLabel = 'Create';
      this.cardLabel = 'New List';
      this.editingData = true;
    },

    setEditListVariables() {
      this.getListByID();
      this.actionToExecute = 'editCustomList';
      this.submitButtonLabel = 'Save';
      this.cardLabel = 'Edit List';
    },

    clearVariables() {
      this.list = null;
      this.originalList = null;
      this.entriesData = null
      this.editingData = false;
      this.permissionDisable = false
      this.copyFromDisable = false
      this.showPermissions = false
    },
  },
}
</script>

<style scoped>
.permissionMatrixDiv {
  margin-top: 16px;
  padding-bottom: 16px;
}

.company {
  height: 100%;
  max-height: 100%;
  background-color: #fff;
  position: relative;
  border-radius: 8px;
}

.company-card {
  height: 100%;
}

.company-card {
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
  border-radius: 8px;
}

.no.company-card {
  display: flex;
  flex-direction: column;
  align-content: center;
  justify-content: center;
}

.no.company-card>span {
  color: #29BDF0;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  line-height: normal;
}

.company-info-container {
  gap: 8px;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  height: 100%;
}

.company-logo {
  background-color: white;
  border-radius: 8px;
  height: 40px;
  width: 40px;
  box-shadow: 1.75px 1.75px 4.75px 0px rgba(0, 0, 0, 0.25);
  margin-right: 16px;
  margin-left: 2px;
}

.edit-image-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 10px 0;
}
</style>



// WEBPACK FOOTER //
// src/newComponents/SystemManagement/Lists/Lists.vue